import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, Tooltip, TextField } from "@mui/material";
import { IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { TablePagination } from "@mui/material";
import Swal from "sweetalert2";
import MuiModal from "../../components/MuiModal";
import { useForm } from "react-hook-form";
import { createShortenUrl, getShortenUrls, deleteShortenUrl } from "./Helper";
import DeleteIcon from "@mui/icons-material/Delete";

const ShortenUrlList = () => {
  const context = useOutletContext();
  const [shortenUrls, setShortenUrls] = useState([]);
  const [urlPage, setUrlPage] = useState(0);
  const [urlLimit, setUrlLimit] = useState(10);
  const [urlTotal, setUrlTotal] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const domain = "https://t.prolegion.com";

  const fetchData = (page, limit) => {
    getShortenUrls(page, limit)
      .then((response) => {
        const responseData = response.data || {};
        const dataArray = responseData.data || [];
        const total = responseData.total || 0;
        
        setShortenUrls(dataArray);
        setUrlTotal(total);
      })
      .catch((error) => {
        console.error("Error fetching short URLs:", error);
        setShortenUrls([]);
        setUrlTotal(0);
        const errorMessage = error.response?.data?.message || error.message || "Failed to fetch short URLs";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonColor: "#2EBAA3",
        });
      });
  };

  const handleChangePage = (event, newPage) => {
    setUrlPage(newPage);
    fetchData(newPage, urlLimit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setUrlLimit(newLimit);
    setUrlPage(0);
    fetchData(0, newLimit);
  };

  useEffect(() => {
    context(["Shorten URL", "Manage Short URLs"]);
    fetchData(urlPage, urlLimit);
  }, []);

  const handleCopy = (slug) => {
    const fullUrl = slug.startsWith('http') ? slug : `${domain}/${slug}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      Swal.fire({
        icon: "success",
        title: "Copied!",
        text: "Short URL copied to clipboard",
        timer: 2000,
        showConfirmButton: false,
      });
    }).catch((err) => {
      console.error("Failed to copy:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to copy URL",
      });
    });
  };

  const handleDelete = (id, slug) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2EBAA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteShortenUrl(id)
          .then((response) => {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Short URL has been deleted.",
              confirmButtonColor: "#2EBAA3",
              timer: 2000,
              showConfirmButton: false,
            });
            fetchData(urlPage, urlLimit);
          })
          .catch((error) => {
            console.error("Error deleting short URL:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to delete short URL";
            Swal.fire({
              icon: "error",
              title: "Error",
              text: errorMessage,
              confirmButtonColor: "#2EBAA3",
            });
          });
      }
    });
  };

  const truncateUrl = (url, maxLength = 60) => {
    if (url.length <= maxLength) {
      return url;
    }
    return url.substring(0, maxLength) + "...";
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      slug: "",
    },
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
    reset();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const onSubmit = (formData) => {
    setIsSubmitting(true);
    
    createShortenUrl({
      originalUrl: formData.slug,
      linkType: "none",
    })
      .then((response) => {
        setIsSubmitting(false);
        handleCloseModal();
        fetchData(urlPage, urlLimit);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Short URL created successfully",
          confirmButtonColor: "#2EBAA3",
        });
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.error("Error creating short URL:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to create short URL";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonColor: "#2EBAA3",
        });
      });
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <div class="flex w-full justify-between">
        <div class="flex-none">
          <h3
            className="col-span-5 text-lg uppercase tracking-wider font-bold inline"
            style={{ color: "#2EBAA3" }}
          >
            Manage Short URLs
          </h3>
        </div>
        <div className="flex flex-row-reverse">
          <div className="px-2 w-52">
            <Button
              type="submit"
              variant="contained"
              style={{ color: "white" }}
              onClick={handleOpenModal}
              fullWidth
            >
              Create Short URL
            </Button>
          </div>
        </div>
      </div>
      <br />
      <div class="overflow-x-auto relative">
        <table class="w-full text-sm text-left text-gray-500">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" class="py-3 px-6 uppercase">
                S No.
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Shorten Slug
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Full URL
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Short URL
              </th>
              <th scope="col" class="py-3 px-6 uppercase text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {shortenUrls?.length > 0 ? (
              shortenUrls.map((item, index) => {
                const slug = item?.slug || item?.shortUrl?.split('/').pop() || item?.shortCode || '';
                const originalUrl = item?.originalUrl || item?.url || item?.longUrl || '';
                const shortUrl = item?.shortUrl || `${domain}/${slug}`;
                const displayShortUrl = shortUrl.startsWith('http') ? shortUrl : `${domain}/${slug}`;
                
                return (
                  <tr class="bg-white border-b" key={item?.id || item?.shortCode || index}>
                    <th
                      scope="row"
                      class="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                    >
                      {urlPage * urlLimit + index + 1}
                    </th>
                    <td class="py-4 px-6">
                      <span className="font-mono text-sm">{slug}</span>
                    </td>
                    <td class="py-4 px-6">
                      {originalUrl && originalUrl.length > 60 ? (
                        <Tooltip title={originalUrl} arrow placement="top">
                          <span className="text-sm block cursor-pointer truncate max-w-md">
                            {truncateUrl(originalUrl)}
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="text-sm block max-w-md">
                          {originalUrl}
                        </span>
                      )}
                    </td>
                    <td class="py-4 px-6">
                      <span className="font-mono text-sm text-blue-600">{displayShortUrl}</span>
                    </td>
                    <td class="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <IconButton
                          aria-label="copy"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCopy(slug);
                          }}
                          title="Copy Short URL"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={(e) => {
                            e.preventDefault();
                            const itemId = item?.id || item?.uuid || item?.shortCode || '';
                            handleDelete(itemId, slug);
                          }}
                          title="Delete Short URL"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="py-4 px-6 text-center text-gray-500">
                  No short URLs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={urlTotal}
        page={urlPage}
        onPageChange={handleChangePage}
        rowsPerPage={urlLimit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
      
      <MuiModal
        open={isModalOpen}
        onCancel={handleCloseModal}
        title="Create Short URL"
        maxWith="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="pt-2">
            <TextField
              className="w-full"
              id="outlined-basic"
              {...register("slug", { 
                required: "URL is required",
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: "Please enter a valid URL starting with http:// or https://"
                }
              })}
              label="FULL URL *"
              margin="normal"
              variant="outlined"
              size="small"
              placeholder="https://www.example.com/very/long/url"
              fullWidth
            />
            {errors.slug && (
              <p className="text-red-500 text-xs">{errors.slug.message}</p>
            )}
            <div className="mt-2 text-sm text-gray-500">
              Short URL will be generated automatically after creation
            </div>
          </div>
          <br />
          <div className="text-right pt-10">
            <Button
              type="button"
              variant="outlined"
              style={{ marginRight: "10px" }}
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              style={{ color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Create Short URL"}
            </Button>
          </div>
        </form>
      </MuiModal>
    </div>
  );
};

export default ShortenUrlList;

